import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import LocationPicker from '../../components/LocationPicker';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import StorageSelect from '../../components/StorageSelect';
import ItemStackView from '../../components/ItemStackView';
import { ProgressBar } from '../../components/ProgressBar';
import { Badge } from '../../components/Badge';
import { SkeletonCard } from '../../components/Skeleton';
import { fromInventorySlot } from '../../services/itemRefAdapters';
import { formatDateTime } from '../../services/dateFormat';
import { firstError } from '../../api/envelope';
import styles from './UnloadArea.module.css';
import spawnerStyles from './SpawnerUpgrade.module.css';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

interface SpawnerUpgradeConfig {
    id: string;
    name: string;
    citybuild: number;
    citybuildName: string;
    location: string;
    range: number;
    sourceStorageId: string;
    isEnabled: boolean;
    createdAt: string;
}

interface KnownSpawner {
    id: string;
    citybuildName: string;
    x: number;
    y: number;
    z: number;
    mobName: string;
    settingsAccessDenied: boolean;
    upgradeOneCompleted: boolean;
    upgradeItemKey: string;
    upgradeRequired: number;
    upgradeDelivered: number;
    dropLevel: number;
    storageLevel: number;
    xpLevel: number;
    lastSeenAt: string;
}

function upgradeItemStack(itemKey: string) {
    const [itemId, damage] = itemKey.split(':').map(part => parseInt(part));
    return fromInventorySlot({ itemId, count: 1, damage: damage || 0 });
}

export default function SpawnerUpgradePage() {
    return <BotScoped groupAware><SpawnerUpgradeInner /></BotScoped>;
}

function SpawnerUpgradeInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const inv = useInventoryApi();
    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';

    const [configs, setConfigs] = createSignal<SpawnerUpgradeConfig[]>([]);
    const [spawners, setSpawners] = createSignal<KnownSpawner[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal(0);
    const [formLocation, setFormLocation] = createSignal('');
    const [formRange, setFormRange] = createSignal(30);
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    async function loadConfigs() {
        const res = await api.spawnerUpgrade.getSpawnerUpgradeConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as unknown as SpawnerUpgradeConfig[]);
    }

    async function loadSpawners() {
        const res = await api.spawnerUpgrade.getKnownSpawners();
        if (isOk(res.data)) setSpawners((res.data.value.spawners ?? []) as unknown as KnownSpawner[]);
    }

    onMount(async () => {
        try {
            const [, , storageList] = await Promise.all([loadConfigs(), loadSpawners(), inv.fetchStorages()]);
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('SPAWNERUPGRADE_LOAD_FAILED'));
        } finally { setLoading(false); }
    });

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormCitybuild(0);
        setFormLocation('');
        setFormRange(30);
        setFormStorageId(storages()[0]?.id ?? '');
        setFormIsEnabled(true);
        setShowForm(true);
    }

    function openEdit(c: SpawnerUpgradeConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormCitybuild(c.citybuild);
        setFormLocation(c.location);
        setFormRange(c.range);
        setFormStorageId(c.sourceStorageId);
        setFormIsEnabled(c.isEnabled);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (!formStorageId()) { toast?.error(t('SPAWNERUPGRADE_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || t('SPAWNERUPGRADE_DEFAULT_NAME'),
            citybuild: formCitybuild(),
            location: formLocation().trim(),
            range: formRange(),
            sourceStorageId: formStorageId(),
            isEnabled: formIsEnabled(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.spawnerUpgrade.updateSpawnerUpgradeConfig(id, body)
                : await api.spawnerUpgrade.createSpawnerUpgradeConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('SPAWNERUPGRADE_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('SPAWNERUPGRADE_UPDATED') : t('SPAWNERUPGRADE_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('SPAWNERUPGRADE_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.spawnerUpgrade.deleteSpawnerUpgradeConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('SPAWNERUPGRADE_DELETE_FAILED'));
            return;
        }
        toast?.info(t('SPAWNERUPGRADE_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('SPAWNERUPGRADE_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.spawnerUpgrade.runSpawnerUpgradeConfig(id, { botId: targetBotId }),
            { success: t('SPAWNERUPGRADE_STARTED'), failure: t('SPAWNERUPGRADE_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.TasksRun);
        if (targets.length === 0) { toast?.error(t('SPAWNERUPGRADE_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.spawnerUpgrade.stopSpawnerUpgrade(id, { botId: targetBotId }),
            { success: t('SPAWNERUPGRADE_STOPPED'), failure: t('SPAWNERUPGRADE_STOP_FAILED') });
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? t('SPAWNERUPGRADE_STORAGE_MISSING');
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="SPAWNERUPGRADE_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="SPAWNERUPGRADE_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="SPAWNERUPGRADE_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.grid}>
                    <SkeletonCard />
                    <SkeletonCard />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="SPAWNERUPGRADE_EDIT"/> : <Text val="SPAWNERUPGRADE_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="SPAWNERUPGRADE_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('SPAWNERUPGRADE_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="SPAWNERUPGRADE_F_CITYBUILD"/></span>
                                <select class={styles.input} value={formCitybuild()} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="SPAWNERUPGRADE_F_LOCATION"/></span>
                                <LocationPicker value={formLocation()} onChange={setFormLocation} optional />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="SPAWNERUPGRADE_F_RANGE"/></span>
                                <input class={styles.input} type="number" min="1" max="512" value={formRange()} onInput={e => setFormRange(parseInt(e.currentTarget.value) || 30)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="SPAWNERUPGRADE_F_STORAGE"/></span>
                                <StorageSelect
                                    storages={storages()}
                                    value={formStorageId()}
                                    onChange={setFormStorageId}
                                    placeholder={t('SPAWNERUPGRADE_SELECT_STORAGE_OPTION')}
                                />
                            </label>
                        </div>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="SPAWNERUPGRADE_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formStorageId()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-arrow-up-right-dots" />
                        <p><Text val="SPAWNERUPGRADE_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>
                                            {c.citybuildName}
                                            {c.location ? ` · ${c.location}` : ''}
                                        </div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="SPAWNERUPGRADE_ENABLED"/> : <Text val="SPAWNERUPGRADE_DISABLED"/>}
                                    </span>
                                </div>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}><i class="fa-solid fa-magnifying-glass" /> {t('SPAWNERUPGRADE_INFO_RANGE', String(c.range))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-warehouse" /> {storageName(c.sourceStorageId)}</div>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="SPAWNERUPGRADE_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>

                <div class={spawnerStyles.sectionHeader}>
                    <h2><Text val="SPAWNERUPGRADE_KNOWN_TITLE"/></h2>
                    <button class="btn btn-secondary btn-sm" onClick={() => void loadSpawners()} title={t('SPAWNERUPGRADE_REFRESH')}>
                        <i class="fa-solid fa-rotate" />
                    </button>
                </div>

                <Show when={spawners().length > 0} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-dungeon" />
                        <p><Text val="SPAWNERUPGRADE_KNOWN_EMPTY"/></p>
                    </div>
                }>
                    <div class={`fcard ${spawnerStyles.tableCard}`}>
                        <table class={spawnerStyles.table}>
                            <thead>
                                <tr>
                                    <th><Text val="SPAWNERUPGRADE_COL_POSITION"/></th>
                                    <th><Text val="SPAWNERUPGRADE_COL_MOB"/></th>
                                    <th><Text val="SPAWNERUPGRADE_COL_UPGRADE"/></th>
                                    <th><Text val="SPAWNERUPGRADE_COL_LEVELS"/></th>
                                    <th><Text val="SPAWNERUPGRADE_COL_SEEN"/></th>
                                </tr>
                            </thead>
                            <tbody>
                                <For each={spawners()}>
                                    {(s) => (
                                        <tr>
                                            <td>
                                                <span class={spawnerStyles.coords}>{s.x} {s.y} {s.z}</span>
                                                <span class={spawnerStyles.muted}>{s.citybuildName}</span>
                                            </td>
                                            <td>{s.mobName || '-'}</td>
                                            <td>
                                                <Show when={!s.settingsAccessDenied} fallback={<Badge tone="error"><Text val="SPAWNERUPGRADE_NO_ACCESS"/></Badge>}>
                                                    <Show when={!s.upgradeOneCompleted} fallback={<Badge tone="success"><Text val="SPAWNERUPGRADE_DONE"/></Badge>}>
                                                        <Show when={s.upgradeRequired > 0} fallback={<span class={spawnerStyles.muted}><Text val="SPAWNERUPGRADE_PROGRESS_UNKNOWN"/></span>}>
                                                            <div class={spawnerStyles.progressCell}>
                                                                <ItemStackView stack={upgradeItemStack(s.upgradeItemKey)} size={24} showTooltip />
                                                                <div class={spawnerStyles.progressBar}>
                                                                    <ProgressBar value={s.upgradeDelivered} max={s.upgradeRequired}
                                                                        label={t('SPAWNERUPGRADE_PROGRESS', String(s.upgradeDelivered), String(s.upgradeRequired))} />
                                                                </div>
                                                            </div>
                                                        </Show>
                                                    </Show>
                                                </Show>
                                            </td>
                                            <td class={spawnerStyles.levels}>
                                                {t('SPAWNERUPGRADE_LEVELS', String(s.dropLevel), String(s.storageLevel), String(s.xpLevel))}
                                            </td>
                                            <td class={spawnerStyles.muted}>{formatDateTime(s.lastSeenAt)}</td>
                                        </tr>
                                    )}
                                </For>
                            </tbody>
                        </table>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
